import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

type CriticalityOption = "critical_path" | "important" | "optional";

const TRIGGER_CLS = "w-full min-w-0 max-w-full overflow-hidden";
const CONTENT_CLS =
  "w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]";

export interface ManualScenarioFormProps {
  projectId: string;
  capabilities: Array<{ id: string; name: string }>;
  personas: Array<{ id: string; name: string; role: string }>;
  initialPersonaId?: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export function ManualScenarioForm({
  projectId,
  capabilities,
  personas,
  initialPersonaId,
  onCancel,
  onSuccess,
}: ManualScenarioFormProps) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const [title, setTitle] = useState("");
  const [capabilityId, setCapabilityId] = useState<string>(
    capabilities[0]?.id ?? "__new__",
  );
  const [newCapName, setNewCapName] = useState("");
  const [criticality, setCriticality] =
    useState<CriticalityOption>("important");
  const [narrative, setNarrative] = useState("");
  const [tension, setTension] = useState("");
  const [humanTests, setHumanTests] = useState<string[]>([""]);
  const [personaId, setPersonaId] = useState<string>(
    initialPersonaId ?? "__none__",
  );
  const [personaBrief, setPersonaBrief] = useState("");

  useEffect(() => {
    if (capabilityId === "__new__" && capabilities.length > 0 && !newCapName) {
      setCapabilityId(capabilities[0].id);
    }
  }, [capabilities, capabilityId, newCapName]);

  const createCapMutation = trpc.capability.create.useMutation({
    onError: (err) => toastTrpcError(err, "Failed to create capability"),
  });
  const createOutlineMutation = trpc.outline.create.useMutation({
    onError: (err) => toastTrpcError(err, "Failed to create scenario"),
  });
  const createScenarioMutation = trpc.scenario.create.useMutation({
    onError: (err) => toastTrpcError(err, "Failed to cast persona"),
  });

  const isSubmitting =
    createCapMutation.isPending ||
    createOutlineMutation.isPending ||
    createScenarioMutation.isPending;
  const isCreatingCap = capabilityId === "__new__";
  const hasValidCap = isCreatingCap
    ? newCapName.trim().length > 0
    : capabilityId.length > 0;
  const canSubmit =
    !isSubmitting &&
    title.trim().length > 0 &&
    narrative.trim().length > 0 &&
    hasValidCap;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      let resolvedCapId = capabilityId;
      if (isCreatingCap) {
        const createdCap = await createCapMutation.mutateAsync({
          projectId,
          name: newCapName.trim(),
        });
        resolvedCapId = createdCap.id;
      }
      const cleanedTests = humanTests.map((q) => q.trim()).filter(Boolean);
      const created = await createOutlineMutation.mutateAsync({
        projectId,
        primaryCapabilityId: resolvedCapId,
        name: title.trim(),
        taskCriticality: criticality,
        narrative: narrative.trim(),
        tension: tension.trim() || null,
        humanTests: cleanedTests.length > 0 ? cleanedTests : undefined,
        language: "en",
      });
      if (personaId && personaId !== "__none__") {
        await createScenarioMutation.mutateAsync({
          outlineId: created.id,
          personaId,
          brief: personaBrief.trim() || undefined,
        });
      }
      await Promise.all([
        utils.outline.list.invalidate({ projectId }),
        utils.capability.list.invalidate({ projectId }),
        utils.scenario.list.invalidate({ projectId }),
      ]);
      toast.success(`Created "${created.name}"`);
      onSuccess();
      navigate(`/projects/${projectId}/scenarios/${created.id}`);
    } catch {
      // Handled by mutation onError callbacks
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="manual-scenario-title">Title</Label>
        <Input
          id="manual-scenario-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Claim with a lost receipt"
          required
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="manual-scenario-capability">Capability</Label>
          <Select value={capabilityId} onValueChange={setCapabilityId}>
            <SelectTrigger
              id="manual-scenario-capability"
              className={TRIGGER_CLS}
            >
              <SelectValue placeholder="Select capability" />
            </SelectTrigger>
            <SelectContent position="popper" className={CONTENT_CLS}>
              {capabilities.map((cap) => (
                <SelectItem key={cap.id} value={cap.id}>
                  {cap.name}
                </SelectItem>
              ))}
              <SelectItem value="__new__">+ New capability…</SelectItem>
            </SelectContent>
          </Select>
          {isCreatingCap && (
            <Input
              value={newCapName}
              onChange={(e) => setNewCapName(e.target.value)}
              placeholder="New capability name (e.g. Submit expenses)"
              className="mt-2"
              required
            />
          )}
        </div>

        <div className="min-w-0 space-y-1.5">
          <Label htmlFor="manual-scenario-criticality">Criticality</Label>
          <Select
            value={criticality}
            onValueChange={(v) => setCriticality(v as CriticalityOption)}
          >
            <SelectTrigger
              id="manual-scenario-criticality"
              className={TRIGGER_CLS}
            >
              <SelectValue placeholder="Select criticality" />
            </SelectTrigger>
            <SelectContent position="popper" className={CONTENT_CLS}>
              <SelectItem value="critical_path">Critical path</SelectItem>
              <SelectItem value="important">Important</SelectItem>
              <SelectItem value="optional">Optional</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="manual-scenario-narrative">Narrative</Label>
        <Textarea
          id="manual-scenario-narrative"
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          placeholder="Describe the human situation and moment of need in domain language."
          rows={4}
          required
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="manual-scenario-tension">Tension (optional)</Label>
        <Input
          id="manual-scenario-tension"
          value={tension}
          onChange={(e) => setTension(e.target.value)}
          placeholder="e.g. Speed vs. getting it right the first time"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Human tests</Label>
        <p className="text-xs text-muted-foreground">
          Yes/no questions each cast persona answers after their run
        </p>
        <div className="space-y-2">
          {humanTests.map((q, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={q}
                onChange={(e) =>
                  setHumanTests((prev) =>
                    prev.map((item, j) => (j === i ? e.target.value : item)),
                  )
                }
                placeholder="e.g. Could they submit the claim without asking anyone?"
              />
              {humanTests.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setHumanTests((p) => p.filter((_, j) => j !== i))
                  }
                  aria-label={`Remove human test ${i + 1}`}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </div>
          ))}
          {humanTests.length < 3 && (
            <button
              type="button"
              onClick={() => setHumanTests((p) => [...p, ""])}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add human test
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2 pt-1">
        <Label htmlFor="manual-scenario-persona">
          Cast initial persona (optional)
        </Label>
        <Select value={personaId} onValueChange={setPersonaId}>
          <SelectTrigger id="manual-scenario-persona" className={TRIGGER_CLS}>
            <SelectValue placeholder="None (cast later)" />
          </SelectTrigger>
          <SelectContent position="popper" className={CONTENT_CLS}>
            <SelectItem value="__none__">None (cast later)</SelectItem>
            {personas.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} — {p.role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {personaId !== "__none__" && (
        <div className="space-y-1.5">
          <Label htmlFor="manual-scenario-brief">
            First-person brief (optional)
          </Label>
          <Input
            id="manual-scenario-brief"
            value={personaBrief}
            onChange={(e) => setPersonaBrief(e.target.value)}
            placeholder="e.g. Standing in the van after a site visit with a crumpled receipt"
          />
        </div>
      )}

      <DialogFooter className="pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isSubmitting ? "Creating…" : "Create scenario"}
        </Button>
      </DialogFooter>
    </form>
  );
}
